import { Cross2Icon, MagnifyingGlassIcon } from "@radix-ui/react-icons";
import { useButton } from "@react-aria/button";
import { useComboBox } from "@react-aria/combobox";
import { useFilter } from "@react-aria/i18n";
import { useSearchField } from "@react-aria/searchfield";
import { useComboBoxState } from "@react-stately/combobox";
import { useSearchFieldState } from "@react-stately/searchfield";
import type { ComboBoxProps } from "@react-types/combobox";
import * as React from "react";
import { ListBox } from "./list-box";
import { Popover } from "./popover";
import { ErrorBoundary } from "./error-boundary";
import { Button } from "./button";

export function SearchAutocomplete<T extends object>(props: ComboBoxProps<T>) {
  const { contains } = useFilter({ sensitivity: "base" });
  const state = useComboBoxState({ ...props, defaultFilter: contains });

  const inputRef = React.useRef<HTMLInputElement>(null);
  const listBoxRef = React.useRef<HTMLUListElement>(null);
  const popoverRef = React.useRef<HTMLDivElement>(null);

  const { inputProps, listBoxProps, labelProps } = useComboBox(
    {
      ...props,
      inputRef,
      listBoxRef,
      popoverRef,
    },
    state
  );

  // Get props for the clear button from useSearchField
  const searchProps = {
    label: props.label,
    value: state.inputValue,
    onChange: (v: string) => state.setInputValue(v),
  };

  const searchState = useSearchFieldState(searchProps);
  const { clearButtonProps } = useSearchField(
    searchProps,
    searchState,
    inputRef
  );
  const clearButtonRef = React.useRef<HTMLButtonElement>(null);
  const { buttonProps } = useButton(clearButtonProps, clearButtonRef);
  const outerRef = React.useRef<HTMLDivElement>(null);

  return (
    <div className="md:w-48 flex-col cursor-text hidden md:inline-flex">
      <label
        {...labelProps}
        className="sr-only block text-left text-sm font-medium text-stone-700"
      >
        {props.label}
      </label>
      <div
        ref={outerRef}
        onClick={() => inputRef.current?.focus()}
        className={`flex flex-row items-center overflow-hidden rounded border border-stone-500 bg-button-normal px-2 shadow-sm outline-1 
        outline-offset-1 outline-stone-200 transition-colors duration-150
        ${state.isFocused ? "outline" : ""}`}
      >
        <MagnifyingGlassIcon
          aria-hidden="true"
          className="pointer-events-none h-5 w-5 cursor-text text-stone-300"
        />
        <input
          {...inputProps}
          ref={inputRef}
          className="text-bl w-full appearance-none bg-button-normal px-3 outline-none"
        />
        <Button
          {...buttonProps}
          ref={clearButtonRef}
          dangerouslySetStyle={{ visibility: state.inputValue !== "" ? "visible" : "hidden" }}
          className="cursor-pointer text-stone-300 hover:text-stone-300 rounded"
        >
          <Cross2Icon aria-hidden="true" className="h-4 w-4" />
        </Button>
      </div>
      <ErrorBoundary title="Popover">
        {state.isOpen && (
          <Popover
            popoverRef={popoverRef}
            triggerRef={outerRef}
            state={state}
            isNonModal
            placement="bottom start"
            className="w-52"
          >
            <ListBox {...listBoxProps} listBoxRef={listBoxRef} state={state} />
          </Popover>
        )}
      </ErrorBoundary>
    </div>
  );
}
